import { useMemo } from "react";
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiConfig, type MutationApiConfig } from "@visioni/a3s-client-api/app";
import {
  deleteReportEmailDeliveryMutation,
  putReportEmailDeliveryMutation,
  reportEmailDeliveryQuery,
  roadsenseExampleWorkbookFilename,
  useReportEmailDeliveryForm,
  type ReportEmailAccessBannerReason,
  type ReportEmailDeliveryFormModel,
} from "@visioni/a3s-reports/app";
import type { ReportEmailDeliveryFrequency, ReportEmailDeliveryState } from "@visioni/a3s-reports/client-messages";
import type { SiteId } from "@visioni/a3s-schema";
import { useSites } from "@src/features/cameras/hooks/useSites";
import { useInheritedAccessReason } from "@src/features/auth/hooks/useInheritedAccessReason";
import { useImpersonationStore } from "@src/features/auth/impersonation-store";
import { useAuth } from "@src/features/tenants";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import { resolvedLocalTimeZone } from "@src/utils/format-date";
import {
  parseEmailDeliverySiteId,
  reportEmailDeliveryFailureMessage,
  reportEmailDeliveryPageStatus,
  reportEmailDeliverySuccessMessage,
  savedReportEmailDelivery,
  shouldRetryReportEmailDeliveryLoad,
  type ReportEmailDeliveryChange,
} from "./report-email-delivery-page";

/** The one report type with report email delivery. */
const REPORT_TYPE = "roadsense";

export interface ReportEmailDeliveryPageReady {
  readonly kind: "ready";
  readonly siteName: string;
  readonly email: string;
  /** What `WhatYoullReceivePanel` shows the attachment saved as. */
  readonly exampleFileName: string;
  readonly accessBannerReason: ReportEmailAccessBannerReason | null;
  /** The run time is shown in the viewer's timezone, for the coming run after `now`. */
  readonly now: Date;
  readonly viewerTimeZone: string;
  readonly form: ReportEmailDeliveryFormModel;
  /** What's saved now: `undefined` when the site isn't set up. */
  readonly saved: ReportEmailDeliveryFrequency | undefined;
  /** A start or a save is in flight. */
  readonly isSubmitting: boolean;
  readonly isStopping: boolean;
  readonly start: (value: ReportEmailDeliveryFrequency) => void;
  readonly save: (value: ReportEmailDeliveryFrequency) => void;
  readonly stop: () => void;
}

export type ReportEmailDeliveryPage =
  | { readonly kind: "loading" }
  /** The site id is malformed, or the site isn't one of the tenant's. */
  | { readonly kind: "notFound" }
  | { readonly kind: "forbidden" }
  | { readonly kind: "failed"; readonly retry: () => void }
  | ReportEmailDeliveryPageReady;

/**
 * Everything the report email delivery page shows for one site: the caller's
 * saved delivery, the form seeded from it, and the start, save and stop
 * actions with their toasts.
 */
export function useReportEmailDeliveryPage(siteIdParam: string | string[] | undefined): ReportEmailDeliveryPage {
  const siteId = parseEmailDeliverySiteId(siteIdParam);
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // While impersonating, the API acts on the impersonated user's delivery, so it goes to them.
  const impersonatedEmail = useImpersonationStore((state) => state.impersonatedEmail);
  const accessBannerReason = useInheritedAccessReason();
  const sitesQuery = useSites({ enabled: siteId !== undefined });

  const deliveryQueryOptions =
    siteId !== undefined ? reportEmailDeliveryQuery(apiConfig, { type: REPORT_TYPE, siteId }) : undefined;
  const deliveryQuery = useQuery({
    queryKey: deliveryQueryOptions?.queryKey ?? [],
    queryFn: deliveryQueryOptions?.queryFn ?? skipToken,
    retry: shouldRetryReportEmailDeliveryLoad,
    // Always refetch on mount: the persisted cache may hold a delivery that
    // was changed since, e.g. from another device or an email's link.
    staleTime: 0,
  });

  const saved = savedReportEmailDelivery(deliveryQuery.data);
  const form = useReportEmailDeliveryForm(saved);

  const mutationConfig = useMemo(
    (): MutationApiConfig =>
      apiConfig.tenant ?
        {
          tenantId: apiConfig.tenant.tenantId,
          apiBaseUrl: apiConfig.tenant.apiBaseUrl,
          authenticatedFetch: apiConfig.authenticatedFetch,
        }
      : { authenticatedFetch: apiConfig.authenticatedFetch },
    [apiConfig.tenant, apiConfig.authenticatedFetch],
  );

  const setSavedState = (state: ReportEmailDeliveryState) => {
    if (deliveryQueryOptions !== undefined) queryClient.setQueryData(deliveryQueryOptions.queryKey, state);
  };

  const putMutation = useMutation({
    mutationFn: ({ value }: { value: ReportEmailDeliveryFrequency; change: ReportEmailDeliveryChange }) =>
      putReportEmailDeliveryMutation(mutationConfig, { type: REPORT_TYPE, siteId: requireSiteId(siteId) }).mutationFn(
        value,
      ),
    onSuccess: (state, { change }) => {
      setSavedState(state);
      showSuccessToast({ message: reportEmailDeliverySuccessMessage(change) });
    },
    onError: (error, { change }) => {
      showErrorToast({ message: reportEmailDeliveryFailureMessage(change, error) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      deleteReportEmailDeliveryMutation(mutationConfig, {
        type: REPORT_TYPE,
        siteId: requireSiteId(siteId),
      }).mutationFn(),
    onSuccess: () => {
      setSavedState({ enabled: false });
      // Drop any unsaved edits too: the page goes back to a new delivery. The
      // reseed on the saved value changing doesn't cover this when the stopped
      // delivery was already the default (weekly on Monday): the form's seed
      // doesn't change, so only this reset clears an edit made before Stop.
      form.reset();
      showSuccessToast({ message: reportEmailDeliverySuccessMessage("stop") });
    },
    onError: (error) => {
      showErrorToast({ message: reportEmailDeliveryFailureMessage("stop", error) });
    },
  });

  const status = reportEmailDeliveryPageStatus({
    siteId,
    delivery: { data: deliveryQuery.data, isError: deliveryQuery.isError, error: deliveryQuery.error },
    sites: { data: sitesQuery.sites, isError: sitesQuery.isError },
  });
  if (status.kind === "failed") {
    return {
      kind: "failed",
      retry: () => {
        if (deliveryQuery.isError) void deliveryQuery.refetch();
        if (sitesQuery.isError) sitesQuery.refetch();
      },
    };
  }
  if (status.kind !== "ready") return status;

  const { siteName } = status;
  const now = new Date();
  // One change at a time: a second press while one is in flight is ignored.
  const isBusy = putMutation.isPending || deleteMutation.isPending;

  return {
    kind: "ready",
    siteName,
    email: impersonatedEmail ?? user?.email ?? "",
    exampleFileName: roadsenseExampleWorkbookFilename(siteName, now),
    accessBannerReason,
    now,
    viewerTimeZone: resolvedLocalTimeZone(),
    form,
    saved,
    isSubmitting: putMutation.isPending,
    isStopping: deleteMutation.isPending,
    start: (value) => {
      if (!isBusy) putMutation.mutate({ value, change: "start" });
    },
    save: (value) => {
      if (!isBusy) putMutation.mutate({ value, change: "save" });
    },
    stop: () => {
      if (!isBusy) deleteMutation.mutate();
    },
  };
}

/** The actions only exist on the ready page, which has a valid site id. */
function requireSiteId(siteId: SiteId | undefined): SiteId {
  if (siteId === undefined) throw new Error("A report email delivery needs a valid site id");
  return siteId;
}
